import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  IconButton,
  TablePagination,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import ConfirmDeleteDialog from "../ConfirmDeleteDialog";
import {
  setEditingEntity,
  setDrawingMode,
  setOwnersLayerKey,
  setFieldsLayerKey,
  setCurrentField,
  setTargetBounds,
  setCropTypes,
  setIrrigationMethods,
} from "../../reducers/MapSlice";
import {
  updateField,
  deleteField,
  getFieldsDashboard,
  getCropTypes,
  getIrrigationMethods,
} from "../../api/FlaskAPI";
import { areaUnitShort, formatArea } from "../../utils/areaUnits";
import { useTranslation } from "react-i18next";
import { dateLocale, useLocalize } from "../../i18n";
import { MAX_CROP_LENGTH } from "../../constants/fieldConstants";
import { formatDateYMD, parseDateYMD } from "../../utils/date";
import { isNilUuid } from "../../utils/funcs";
import AvailableDatePicker from "../AvailableDatePicker";
import AlertsResetWarning from "../AlertsResetWarning";
import FieldPanel from "../FieldPanel";
import {
  alertKeys,
  chartKeys,
  fieldKeys,
  fieldStatsKeys,
} from "../../api/queryKeys";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import "./DashboardPage.css";
import { ComplianceBadge } from "../MapPopup";
import { AlertsBadge, TableFilters, NameWithId } from "./TableCells";
import SummaryCards, { sumArea } from "./SummaryCards";
import GrassOutlinedIcon from "@mui/icons-material/GrassOutlined";
import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";

// sorts by owner, area, irrigation, compliance and alerts only.
const columns = [
  { key: "owner", label: "Owner", sortable: true, type: "text" },
  { key: "area", label: "Area", sortable: true, type: "number" },
  { key: "crop_en", label: "Crop", type: "text" },
  { key: "season", label: "Season", type: "text" },
  {
    key: "irrigation_method_en",
    label: "Irrigation",
    sortable: true,
    type: "text",
  },
  { key: "compliant", label: "Compliance", sortable: true, type: "boolean" },
  { key: "alerts", label: "Alerts", sortable: true, type: "boolean" },
];

const capitalize = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "-";
const formatDay = (ymd, language) =>
  ymd
    ? new Date(`${ymd}T00:00:00`).toLocaleDateString(dateLocale(language), {
        day: "numeric",
        month: "short",
        year: "2-digit",
      })
    : "?";

// combines them with "or".
const FILTER_OPTIONS = [
  { key: "alerts", Icon: NotificationsActiveOutlinedIcon },
  { key: "compliant", Icon: GppMaybeOutlinedIcon },
];

const FieldsPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });

  // Pagination state (page is 0-based for TablePagination)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Alert filter dropdown state
  const [selectedAlertFilters, setSelectedAlertFilters] = useState([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const localize = useLocalize();
  const loadErrorMessage = t("fieldsPage.loadFailed");
  const queryClient = useQueryClient();
  const cropTypes = useSelector((state) => state.mapState.cropTypes);
  const irrigationMethods = useSelector(
    (state) => state.mapState.irrigationMethods,
  );
  const selectedField = useSelector((state) => state.mapState.currentField);
  const areaUnit = useSelector((state) => state.settings.areaUnit);

  // Load crop types and irrigation methods if not already loaded (eg. visiting the dashboard directly)
  useEffect(() => {
    if (cropTypes.length === 0) {
      getCropTypes()
        .then((res) => dispatch(setCropTypes(res.data)))
        .catch(() =>
          toast.error(t("fieldsPage.toasts.cropsFailed"), {
            toastId: "crops",
          }),
        );
    }
    if (irrigationMethods.length === 0) {
      getIrrigationMethods()
        .then((res) => dispatch(setIrrigationMethods(res.data)))
        .catch(() =>
          toast.error(t("fieldsPage.toasts.irrigationFailed"), {
            toastId: "irrigation-methods",
          }),
        );
    }
  }, [cropTypes.length, irrigationMethods.length, dispatch, t]);

  // Debounce the search box so each keystroke doesn't hit the backend
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const params = useMemo(() => {
    const params = { page: page + 1, per_page: rowsPerPage };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (sortConfig.key) {
      params.sort_by = sortConfig.key;
      params.sort_dir = sortConfig.direction;
    }
    if (selectedAlertFilters.length > 0) {
      params.filter = selectedAlertFilters.join(",");
    }
    params.area_unit = areaUnit;
    return params;
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    sortConfig,
    selectedAlertFilters,
    areaUnit,
  ]);

  // keepPreviousData leaves the current rows on screen while the next page,
  // search or sort loads, instead of blanking the table to a spinner.
  const {
    data,
    isPending: loading,
    isError,
    isFetching,
  } = useQuery({
    queryKey: fieldKeys.list(params),
    queryFn: ({ signal }) =>
      getFieldsDashboard(params, signal).then((res) => res.data),
    placeholderData: keepPreviousData,
    meta: { errorMessage: loadErrorMessage },
  });
  const fields = data?.items || [];
  const totalCount = data?.total || 0;

  // count the whole project, not the current page or search.
  const allParams = { area_unit: areaUnit };
  const { data: allData } = useQuery({
    queryKey: fieldKeys.list(allParams),
    queryFn: ({ signal }) =>
      getFieldsDashboard(allParams, signal).then((res) => res.data),
  });
  const allFields = allData?.items;
  const withAlerts = allFields?.filter((f) => f.alerts).length;
  const notCompliant = allFields?.filter((f) => !f.compliant).length;
  const summary = [
    {
      label: t("dashboard.nav.fields"),
      value: allFields?.length,
      Icon: GrassOutlinedIcon,
    },
    {
      label: t("dashboard.cards.totalArea"),
      value:
        allFields &&
        formatArea(sumArea(allFields), areaUnit, { withUnit: false }),
      note: areaUnitShort(areaUnit),
      Icon: SquareFootOutlinedIcon,
    },
    {
      label: t("dashboard.cards.withOpenAlerts"),
      value: withAlerts,
      Icon: NotificationsActiveOutlinedIcon,
      tone: withAlerts ? "warning" : "neutral",
    },
    {
      label: t("dashboard.cards.notCompliant"),
      value: notCompliant,
      Icon: GppMaybeOutlinedIcon,
      tone: notCompliant ? "error" : "neutral",
    },
  ];
  const error = isError ? loadErrorMessage : null;

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
      key = null;
    }
    setSortConfig({ key, direction });
    setPage(0);
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };

  // Alert filter dropdown handlers

  // Dialog handlers
  const handleRowClick = (field) => {
    dispatch(setCurrentField(field));
    setIsEditing(false);
    setDialogOpen(true);
  };

  // Called by FieldPanel after alert changes: refetch the list, then keep the
  // open field in sync with its refreshed row.
  const refreshDashbord = async () => {
    await queryClient.invalidateQueries({ queryKey: fieldKeys.all });
    const updatedSelected = queryClient
      .getQueryData(fieldKeys.list(params))
      ?.items?.find((field) => field.id === selectedField?.id);
    if (updatedSelected) {
      dispatch(setCurrentField(updatedSelected));
    }
  };

  const handleCloseDialog = () => {
    // `currentField` is left: so closing the dialog keeps the field selected for the map panel.
    setDialogOpen(false);
    setIsEditing(false);
    setEditForm({});
  };

  const handleShowArea = () => {
    try {
      dispatch(setTargetBounds(selectedField.bounds));
      setDialogOpen(false);
      navigate("/");
    } catch (err) {
      console.error("Error fetching field boundaries:", err);
      toast.error(t("fieldsPage.toasts.boundsFailed"));
    }
  };

  const handleStartEdit = async () => {
    if (!selectedField) return;
    setIsEditing(true);
    setEditForm({
      ...selectedField,
      other_crop: isNilUuid(selectedField.crop_id)
        ? localize(selectedField, "crop")
        : "",
    });
  };

  const handleSaveEdit = async () => {
    if (!selectedField) return;
    if (!editForm.crop_id) {
      toast.error(t("area.toasts.crop"));
      return;
    }
    if (isNilUuid(editForm.crop_id)) {
      if (!editForm.other_crop) {
        toast.error(t("area.toasts.otherCrop"));
        return;
      }
      if (editForm.other_crop.length > MAX_CROP_LENGTH) {
        toast.error(t("area.toasts.cropMax", { max: MAX_CROP_LENGTH }));
        return;
      }
    }
    if (!editForm.sos) {
      toast.error(t("area.toasts.sos"));
      return;
    }
    if (!editForm.eos) {
      toast.error(t("area.toasts.eos"));
      return;
    }
    if (editForm.sos > editForm.eos) {
      toast.error(t("area.toasts.sosAfterEos"));
      return;
    }
    if (!editForm.irrigation_method_id) {
      toast.error(t("area.toasts.irrigation"));
      return;
    }

    setSaving(true);
    try {
      const response = await updateField({
        id: selectedField.id,
        crop_id: editForm.crop_id,
        other_crop: editForm.other_crop,
        sos: editForm.sos,
        eos: editForm.eos,
        irrigation_method_id: editForm.irrigation_method_id,
        coordinates: null,
        area_unit: areaUnit,
      });
      if (response.status === 200 || response.status === 204) {
        toast.success(response.data.message);
        const updatedField = response.data.updated_field;
        // Saving a field recalculates its alerts and moves what its charts plot.
        queryClient.invalidateQueries({ queryKey: alertKeys.all });
        queryClient.invalidateQueries({ queryKey: fieldStatsKeys.all });
        queryClient.invalidateQueries({ queryKey: chartKeys.all });
        await queryClient.invalidateQueries({ queryKey: fieldKeys.all });
        dispatch(setFieldsLayerKey());
        dispatch(setOwnersLayerKey());
        dispatch(setCurrentField(updatedField));
        setIsEditing(false);
      } else {
        toast.error(t("fieldsPage.toasts.updateFailed"));
      }
    } catch (error) {
      const msg =
        error.response?.data?.message || t("fieldsPage.toasts.updateFailed");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleEditBoundary = () => {
    if (!selectedField) return;
    if (!editForm || Object.keys(editForm).length === 0) return;
    try {
      dispatch(setTargetBounds(selectedField.bounds));
      dispatch(
        setEditingEntity({
          id: selectedField.id,
          properties: editForm,
        }),
      );
      dispatch(setDrawingMode("field"));
      setDialogOpen(false);
      navigate("/");
    } catch (err) {
      console.error("Error fetching field boundaries:", err);
      toast.error(t("fieldsPage.toasts.boundsEditFailed"));
    }
  };

  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
  };

  const handleCloseConfirmDelete = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedField) return;
    setDeleting(true);
    try {
      const response = await deleteField(selectedField.id);
      if (response.status === 200 || response.status === 204) {
        toast.success(t("panel.deleted"));
        queryClient.invalidateQueries({ queryKey: alertKeys.all });
        queryClient.invalidateQueries({ queryKey: fieldStatsKeys.all });
        queryClient.invalidateQueries({ queryKey: chartKeys.all });
        // Waiting for the refetch keeps the deleted row from flashing back.
        await queryClient.invalidateQueries({ queryKey: fieldKeys.all });
        setConfirmDeleteOpen(false);
        setDialogOpen(false);
        // clear it everywhere, including the map panel
        dispatch(setCurrentField(null));

        dispatch(setFieldsLayerKey());
      } else {
        const msg = response.data?.message || t("panel.deleteFailed");
        toast.error(msg);
      }
    } catch (error) {
      const msg = error.response?.data?.message || t("panel.deleteFailed");
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // Render cell content based on column type
  const renderCellContent = (field, column) => {
    const value = field[column.key];

    if (column.key === "owner") {
      return <NameWithId name={field.owner} id={field.id} />;
    }
    if (column.key === "crop_en") return capitalize(localize(field, "crop"));
    if (column.key === "irrigation_method_en") {
      return localize(field, "irrigation_method") || "-";
    }
    if (column.key === "season") {
      const language = i18n.resolvedLanguage;
      return `${formatDay(field.sos, language)} – ${formatDay(field.eos, language)}`;
    }
    if (column.key === "compliant") {
      return <ComplianceBadge compliant={value} />;
    }
    if (column.key === "alerts") return <AlertsBadge hasAlerts={value} />;

    // Unit is in the header, cells show the number only
    if (column.key === "area") {
      return formatArea(value, areaUnit, { withUnit: false });
    }

    // Default - just show the value
    return value || "-";
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h1>{t("fieldsPage.title")}</h1>
          <p>{t("fieldsPage.subtitle")}</p>
        </div>
      </div>

      <SummaryCards cards={summary} />

      {/* Controls Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {/* Search */}
        <TextField
          placeholder={t("dashboard.searchFields")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 300 },
            backgroundColor: "white",
            borderRadius: "8px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
        <TableFilters
          options={FILTER_OPTIONS}
          selected={selectedAlertFilters}
          onChange={(next) => {
            setSelectedAlertFilters(next);
            setPage(0);
          }}
        />
      </Box>

      {/* Results count */}
      <Typography variant="body2" sx={{ mb: 2, color: "#6b7280" }}>
        {t("fieldsPage.showing", { shown: fields.length, total: totalCount })}
        {selectedAlertFilters.length > 0 &&
          ` · ${FILTER_OPTIONS.filter((o) =>
            selectedAlertFilters.includes(o.key),
          )
            .map((o) => t(`dashboard.filters.phrases.${o.key}`))
            .join(` ${t("dashboard.filters.or")} `)}`}
      </Typography>

      {/* Reserves the bar's height so the table doesn't jump when it appears. */}
      <Box sx={{ height: 4, mb: 1 }}>
        {isFetching && !loading && <LinearProgress />}
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!loading && !error && fields.length === 0 && (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          <Typography variant="h6" sx={{ color: "#6b7280", mb: 1 }}>
            {t("fieldsPage.noneFound")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#9ca3af" }}>
            {searchQuery || selectedAlertFilters.length > 0
              ? t("dashboard.tryAdjusting")
              : t("fieldsPage.noData")}
          </Typography>
        </Paper>
      )}

      {/* Table */}
      {!loading && !error && fields.length > 0 && (
        <>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: "8px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              overflowX: "auto",
            }}
          >
            <Table size="small" sx={{ "& th, & td": { px: 1.5 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || "left"}
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        whiteSpace: "nowrap",
                        cursor: column.sortable ? "pointer" : "default",
                        userSelect: "none",
                        "&:hover": column.sortable
                          ? { backgroundColor: "#e5e7eb" }
                          : {},
                      }}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {column.key === "area"
                          ? t("dashboard.areaHeader", {
                              unit: areaUnitShort(areaUnit),
                            })
                          : t(`fieldsPage.columns.${column.key}`)}
                        {column.sortable && getSortIcon(column.key)}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow
                    key={field.id || index}
                    onClick={() => handleRowClick(field)}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "white" : "#fafafa",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#e8f4f8" },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.align || "left"}
                        sx={{
                          whiteSpace: "nowrap",
                          color: "#4b5563",
                        }}
                      >
                        {renderCellContent(field, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      {/* Field Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "80vw",
            height: "80vh",
            maxWidth: "none",
            maxHeight: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            pr: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {isEditing ? t("area.titles.editField") : t("fieldsPage.dialogTitle")}
          <IconButton
            onClick={handleCloseDialog}
            size="small"
            aria-label={t("common.close")}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#b0b0b0",
              borderRadius: 4,
            },
          }}
        >
          {isEditing ? (
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              <Autocomplete
                options={cropTypes}
                getOptionLabel={(option) => localize(option, "name")}
                value={cropTypes.find((o) => o.id === editForm.crop_id) || null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    setEditForm((prev) => ({ ...prev, crop_id: undefined }));
                  } else {
                    setEditForm((prev) => ({
                      ...prev,
                      crop_id: newValue.id,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("terms.crop")}
                    size="small"
                    required
                  />
                )}
                size="small"
                fullWidth
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              <Autocomplete
                options={irrigationMethods}
                getOptionLabel={(option) => localize(option, "name")}
                value={
                  irrigationMethods.find(
                    (o) => o.id === editForm.irrigation_method_id,
                  ) || null
                }
                onChange={(_, newValue) =>
                  setEditForm((prev) => ({
                    ...prev,
                    irrigation_method_id: newValue ? newValue.id : undefined,
                  }))
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("area.irrigationMethod")}
                    size="small"
                    required
                  />
                )}
                size="small"
                fullWidth
              />
              {isNilUuid(editForm.crop_id) && (
                <TextField
                  label={t("area.otherCrop")}
                  value={editForm.other_crop || ""}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      other_crop: e.target.value,
                    }))
                  }
                  fullWidth
                  size="small"
                  required
                  inputProps={{ maxLength: MAX_CROP_LENGTH }}
                  helperText={`${(editForm.other_crop || "").length}/${MAX_CROP_LENGTH}`}
                />
              )}
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                >
                  {t("area.sos")}
                </Typography>
                <AvailableDatePicker
                  value={parseDateYMD(editForm.sos)}
                  onChange={(date) =>
                    setEditForm((prev) => ({
                      ...prev,
                      sos: date ? formatDateYMD(date) : "",
                    }))
                  }
                  style={{ width: "100%" }}
                  size="sm"
                />
              </Box>
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                >
                  {t("area.eos")}
                </Typography>
                <AvailableDatePicker
                  value={parseDateYMD(editForm.eos)}
                  onChange={(date) =>
                    setEditForm((prev) => ({
                      ...prev,
                      eos: date ? formatDateYMD(date) : "",
                    }))
                  }
                  style={{ width: "100%" }}
                  size="sm"
                />
              </Box>
              <AlertsResetWarning />
            </Box>
          ) : (
            selectedField && (
              <FieldPanel dialogMode={true} refreshDashbord={refreshDashbord} />
            )
          )}
        </DialogContent>
        <DialogActions>
          {isEditing ? (
            <>
              <Button
                onClick={() => {
                  setIsEditing(false);
                  setEditForm({});
                }}
                color="inherit"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleEditBoundary}
                variant="outlined"
                color="primary"
              >
                {t("dashboard.editBoundary")}
              </Button>
              <Button
                onClick={handleSaveEdit}
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("dashboard.save")
                )}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog} color="inherit">
                {t("common.close")}
              </Button>
              <Button
                onClick={handleShowArea}
                variant="contained"
                color="primary"
              >
                {t("dashboard.showArea")}
              </Button>
              <Button
                onClick={handleStartEdit}
                variant="outlined"
                startIcon={<EditIcon />}
              >
                {t("dashboard.edit")}
              </Button>
              <Button
                onClick={handleDeleteClick}
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
              >
                {t("common.delete")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        elementId={selectedField?.id}
        handleConfirmDelete={handleConfirmDelete}
        isDialogOpen={confirmDeleteOpen}
        handleOnClose={handleCloseConfirmDelete}
        deleting={deleting}
      />
    </div>
  );
};

export default FieldsPage;
